'use client'

import { createBrowserClient } from '@supabase/ssr'
import {
  REALTIME_SUBSCRIBE_STATES,
  type RealtimePostgresChangesPayload
} from '@supabase/supabase-js'
import type { Database } from '@/types/supabase'
import {
  parseFixtureScheduleInvalidation,
  type FixtureScheduleInvalidation
} from '@/lib/realtime/fixtureScheduleUpdates'
import { loadBrowserSupabaseRuntimeConfig } from './browserRuntimeConfig'

type NotificationRow = Database['public']['Tables']['notifications']['Row']
type MatchEventRow = Database['public']['Tables']['realtime_match_events']['Row']
type SubscriptionStatus = Parameters<
  ReturnType<ReturnType<typeof createBrowserClient<Database>>['channel']>['subscribe']
>[0] extends ((status: infer Status, ...args: never[]) => unknown) | undefined
  ? Status
  : never

const MAX_MATCH_IDS = 100
const SAFE_FILTER_VALUE = /^[A-Za-z0-9_-]{1,128}$/
const SAFE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
type RawBrowserClient = ReturnType<typeof createBrowserClient<Database>>

let browserClientPromise: Promise<RawBrowserClient> | null = null

async function loadRealtimeClient(): Promise<RawBrowserClient> {
  if (browserClientPromise) return browserClientPromise

  browserClientPromise = loadBrowserSupabaseRuntimeConfig()
    .then(({ key, url }) =>
      createBrowserClient<Database>(url, key, {
        auth: {
          autoRefreshToken: true,
          detectSessionInUrl: true,
          flowType: 'pkce',
          persistSession: true
        },
        global: {
          headers: { 'X-Client-Info': 'neuroplay-fantasy-football-browser-realtime' }
        }
      })
    )
    .catch((error) => {
      browserClientPromise = null
      throw error
    })

  return browserClientPromise
}

/**
 * Narrow accessor for the shared memoized browser Supabase client. Dashboard
 * live Broadcast (`lib/realtime/supabaseDashboardLiveTransport.ts`) subscribes
 * to a provider-authorized private topic directly and needs the raw client's
 * `channel`/`getChannels`/`removeChannel` surface, unlike the receive-only
 * helpers below that fully own their subscription lifecycle.
 */
export function getBrowserRealtimeClient(): Promise<RawBrowserClient> {
  return loadRealtimeClient()
}

function requireSafeFilterValue(value: string, name: string): string {
  if (!SAFE_FILTER_VALUE.test(value)) {
    throw new Error(`${name} must be a non-empty safe identifier.`)
  }
  return value
}

function requireNonce(value: string): string {
  return requireSafeFilterValue(value, 'nonce')
}

function requireUuid(value: string, name: string): string {
  if (!SAFE_UUID.test(value)) throw new Error(`${name} must be a valid UUID.`)
  return value
}

export type NotificationInvalidationSubscription = {
  userId: string
  nonce: string
  onInsert: (payload: RealtimePostgresChangesPayload<NotificationRow>) => void
  onUpdate: (payload: RealtimePostgresChangesPayload<NotificationRow>) => void
  onStatus?: (status: SubscriptionStatus) => void
}

/** Receive-only invalidations for the authenticated owner's notification feed. */
export function subscribeToNotificationInvalidations({
  userId,
  nonce,
  onInsert,
  onUpdate,
  onStatus
}: NotificationInvalidationSubscription): () => void {
  const safeUserId = requireSafeFilterValue(userId, 'userId')
  const topicPrefix = `realtime:user:${safeUserId}:notifications`
  let disposed = false
  let cleanup: (() => void) | null = null

  void loadRealtimeClient()
    .then((client) => {
      if (disposed) return
      try {
        for (const existing of client.getChannels()) {
          if (existing.topic.startsWith(topicPrefix)) void client.removeChannel(existing)
        }
      } catch {
        // The unique topic suffix still prevents channel reuse if best-effort cleanup fails.
      }

      const channel = client
        .channel(`user:${safeUserId}:notifications:${requireNonce(nonce)}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${safeUserId}`
          },
          onInsert
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${safeUserId}`
          },
          onUpdate
        )
        .subscribe(onStatus)

      cleanup = () => {
        void client.removeChannel(channel)
      }
      if (disposed) cleanup()
    })
    .catch(() => {
      onStatus?.(REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR)
    })

  return () => {
    disposed = true
    if (cleanup) cleanup()
  }
}

export type MatchEventInvalidationSubscription = {
  instanceId: string
  gameweek: number
  matchIds: string[]
  nonce: string
  onInsert: (payload: RealtimePostgresChangesPayload<MatchEventRow>) => void
  onStatus?: (status: SubscriptionStatus) => void
}

/** Receive-only invalidations for at most 100 exact live matches. */
export function subscribeToMatchEventInvalidations({
  instanceId,
  gameweek,
  matchIds,
  nonce,
  onInsert,
  onStatus
}: MatchEventInvalidationSubscription): () => void {
  const safeInstanceId = requireSafeFilterValue(instanceId, 'instanceId')
  if (!Number.isInteger(gameweek) || gameweek < 1 || gameweek > 100) {
    throw new Error('gameweek must be an integer between 1 and 100.')
  }
  if (matchIds.length === 0) throw new Error('matchIds must not be empty.')
  if (matchIds.length > MAX_MATCH_IDS) {
    throw new Error(`matchIds must contain at most ${MAX_MATCH_IDS} entries.`)
  }
  const safeMatchIds = [...new Set(matchIds.map((id) => requireSafeFilterValue(id, 'matchId')))]
  let disposed = false
  let cleanup: (() => void) | null = null

  void loadRealtimeClient()
    .then((client) => {
      if (disposed) return
      const channel = client
        .channel(`dashboard-live:${safeInstanceId}:${gameweek}:${requireNonce(nonce)}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'realtime_match_events',
            // Delivery optimization only. RLS remains the authorization boundary.
            filter: `match_id=in.(${safeMatchIds.join(',')})`
          },
          onInsert
        )
        .subscribe(onStatus)

      cleanup = () => {
        void client.removeChannel(channel)
      }
      if (disposed) cleanup()
    })
    .catch(() => {
      onStatus?.(REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR)
    })

  return () => {
    disposed = true
    if (cleanup) cleanup()
  }
}

export type FixtureScheduleInvalidationSubscription = {
  instanceId: string
  seasonId: string
  onInvalidation: (invalidation: FixtureScheduleInvalidation) => void
  onStatus?: (status: SubscriptionStatus) => void
}

/** Receive-only public Broadcast invalidations containing non-sensitive schedule revision metadata. */
export function subscribeToFixtureScheduleInvalidations({
  instanceId,
  seasonId,
  onInvalidation,
  onStatus
}: FixtureScheduleInvalidationSubscription): () => void {
  requireUuid(instanceId, 'instanceId')
  const safeSeasonId = requireUuid(seasonId, 'seasonId')
  const topic = `season:${safeSeasonId}:fixture-schedule`
  let disposed = false
  let cleanup: (() => void) | null = null

  void loadRealtimeClient()
    .then((client) => {
      if (disposed) return
      try {
        for (const existing of client.getChannels()) {
          if (existing.topic.endsWith(topic)) void client.removeChannel(existing)
        }
      } catch {
        // Best-effort cleanup only; duplicate invalidations remain safe and idempotent.
      }

      const channel = client
        .channel(topic)
        .on('broadcast', { event: 'fixture_schedule_changed' }, ({ payload }) => {
          const invalidation = parseFixtureScheduleInvalidation(payload)
          if (invalidation) onInvalidation(invalidation)
        })
        .subscribe(onStatus)

      cleanup = () => {
        void client.removeChannel(channel)
      }
      if (disposed) cleanup()
    })
    .catch(() => {
      onStatus?.(REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR)
    })

  return () => {
    disposed = true
    if (cleanup) cleanup()
  }
}
